/**
 * 카트 페이지 - 구매 제한 체크 (계정당 + 주문당 둘 다)
 *
 * 두 종류의 제한:
 *   accountMax = data-purchase-limit-max (메타필드 custom.purchase_limit_max_per_account)
 *     → 계정당 최대 (누적 구매 + 현재 카트 합 한도). API current_count(purchased) 차감 필요.
 *   orderMax   = data-order-limit-max (메타필드 custom._purchase_quantity_limit)
 *     → 주문당 최대 (현재 카트만의 한도).
 *
 * 효과적 available = min(accountMax - purchased, orderMax)
 * 둘 중 더 엄격한 쪽이 적용되며, 카트 내 수량 >= available → + 버튼 비활성화.
 */

/**
 * 두 제한을 모두 반영한 추가 가능 수량.
 * 어느 쪽도 설정 안 되어 있으면 Infinity (제한 없음).
 */
function getEffectiveAvailable({ accountMax, purchased, orderMax }) {
  const candidates = [];
  if (accountMax && !isNaN(accountMax)) {
    candidates.push(Math.max(0, accountMax - (purchased || 0)));
  }
  if (orderMax && !isNaN(orderMax)) {
    candidates.push(orderMax);
  }
  if (candidates.length === 0) return Infinity;
  return Math.min(...candidates);
}

async function applyAutoUpdates(autoUpdates) {
  const updates = {};
  autoUpdates.forEach(update => {
    updates[update.variantId] = update.quantity;
  });

  console.log('[PurchaseLimit] Auto-adjustment request:', JSON.stringify(updates));

  try {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('[PurchaseLimit] Cart update failed:', res.status, errorData);
      return false;
    }
    console.log('[PurchaseLimit] Auto-adjustment success');
    return true;
  } catch (e) {
    console.error('[PurchaseLimit] Cart update error:', e);
    return false;
  }
}

/**
 * API에서 상품별 current_count 조회
 */
async function fetchPurchaseCounts(productIds) {
  if (!productIds.length || !window.CustomApi?.checkPurchaseLimit) return {};

  try {
    const customerId = document.querySelector('[customer-main-id]')?.getAttribute('customer-main-id');
    const customerEmail = document.querySelector('cart-form')?.getAttribute('data-customer-email');
    console.log('[PurchaseLimit] API request - productIds:', productIds, '| customerId:', customerId, '| email:', customerEmail);
    const limitRes = await window.CustomApi.checkPurchaseLimit(productIds, customerId, customerEmail);
    const data = await limitRes.json();
    console.log('[PurchaseLimit] API response:', JSON.stringify(data));
    // API는 gid://shopify/Product/123 형식, 프론트는 숫자 ID 사용 → 변환
    const results = {};
    if (data.results) {
      for (const [gid, value] of Object.entries(data.results)) {
        const numericId = gid.replace('gid://shopify/Product/', '');
        results[numericId] = value;
      }
    }
    return results;
  } catch (e) {
    console.error('[PurchaseLimit] API error:', e);
    return {};
  }
}

/**
 * 상품별 { accountMax, purchased, orderMax } 캐시
 * 연속 클릭 차단 시 동기적으로 한도를 판단하기 위함
 */
const __purchaseLimitCache = {};

async function refreshPurchaseLimitCache() {
  // 두 메타필드 중 하나라도 있는 모든 카트 아이템 수집
  const elements = document.querySelectorAll('[data-cart-items-main]');
  const productIds = [...new Set(
    Array.from(elements)
      .filter(el => parseInt(el.getAttribute('data-purchase-limit-max'), 10) || parseInt(el.getAttribute('data-order-limit-max'), 10))
      .map(el => el.getAttribute('product-id'))
  )];
  if (productIds.length === 0) return;

  // accountMax가 설정된 상품만 API 호출 대상
  const accountLimitedIds = [...new Set(
    Array.from(elements)
      .filter(el => parseInt(el.getAttribute('data-purchase-limit-max'), 10))
      .map(el => el.getAttribute('product-id'))
  )];
  const apiResults = accountLimitedIds.length > 0 ? await fetchPurchaseCounts(accountLimitedIds) : {};

  productIds.forEach(pid => {
    const el = document.querySelector(`[data-cart-items-main][product-id="${pid}"]`);
    if (!el) return;
    const accountMax = parseInt(el.getAttribute('data-purchase-limit-max'), 10);
    const orderMax = parseInt(el.getAttribute('data-order-limit-max'), 10);
    __purchaseLimitCache[pid] = {
      accountMax: isNaN(accountMax) ? null : accountMax,
      purchased: apiResults[pid]?.current_count || 0,
      orderMax: isNaN(orderMax) ? null : orderMax,
    };
  });
}

/**
 * 연속 클릭 가드 - 캡처 단계에서 theme.js cart-form 위임 핸들러보다 먼저 실행
 *
 * theme.js는 + 클릭 즉시 input.value를 증가시키고 700ms 디바운스 후 AJAX 호출함.
 * applyPurchaseLimits로 quantity-up--disabled 클래스가 붙는 건 main-cart:quantitychanged 이벤트 후(약 2~3초).
 * 그 공백 동안 연속 클릭 시 한도 초과로 수량이 증가해버리는 문제 → 동기적으로 예측 차단.
 */
function setupRapidClickGuard() {
  document.addEventListener('click', (e) => {
    const plusBtn = e.target.closest('[data-main-cart-quantity-plus-button]');
    if (!plusBtn) return;

    const container = plusBtn.closest('[data-cart-items-main]');
    if (!container) return;

    const productId = container.getAttribute('product-id');
    const info = __purchaseLimitCache[productId];
    if (!info) return; // 메타필드 미설정 또는 캐시 미스 → 차단 안 함

    // 같은 product_id의 모든 variant input 합산 (theme.js가 클릭 직후 input.value를 증가시킨 상태)
    // ⚠ 셀렉터 주의: [data-main-cart-quantity-button]은 +/- 버튼(<a>)에 붙어있음.
    //   실제 수량 input은 [data-main-cart-quantity] (또는 .cart-item__quantity-input).
    let cartQty = 0;
    document.querySelectorAll(`[data-cart-items-main][product-id="${productId}"]`).forEach(el => {
      const input = el.querySelector('.cart-item__quantity-input');
      cartQty += parseInt(input?.value, 10) || 0;
    });

    const available = getEffectiveAvailable(info);

    if (cartQty + 1 > available) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // 시각적으로도 즉시 비활성화 (theme.js의 quantity-up--disabled 분기를 이후 클릭에도 활용)
      plusBtn.classList.add('quantity-up--disabled');
      plusBtn.style.pointerEvents = 'none';
      plusBtn.style.opacity = '0.5';

      console.log('[PurchaseLimit] Cart - rapid click blocked. productId:', productId,
                  '| accountMax:', info.accountMax, '| purchased:', info.purchased, '| orderMax:', info.orderMax,
                  '| cartQty:', cartQty, '| available:', available);
    }
  }, true); // capture phase
}

document.addEventListener('DOMContentLoaded', async () => {
  const submitButton = document.querySelector('[data-submit-cart]');
  const submitButtonDisabled = document.querySelector('[data-submit-cart-disabled]');
  const plusButtons = document.querySelectorAll('[data-main-cart-quantity-plus-button]');
  const minusButtons = document.querySelectorAll('[data-main-cart-quantity-minus-button]');

  const updateSubmitButtonState = (isValid) => {
    try {
      if (submitButton) submitButton.style.display = isValid ? 'flex' : 'none';
      if (submitButtonDisabled) submitButtonDisabled.style.display = isValid ? 'none' : 'flex';
    } catch (e) {
      console.error('[PurchaseLimit] Submit button error:', e);
    }
  };

  /**
   * 결제 버튼 + 플러스 버튼 상태 업데이트 (한 번의 API 호출로 처리)
   */
  const applyPurchaseLimits = async () => {
    try {
      const res = await fetch('/cart.js');
      const cartData = await res.json();

      // 상품별 카트 내 총 수량
      const productQtyMap = {};
      cartData.items.forEach(item => {
        const pid = item.product_id.toString();
        productQtyMap[pid] = (productQtyMap[pid] || 0) + item.quantity;
      });

      // API에서 current_count 조회
      const apiResults = await fetchPurchaseCounts(Object.keys(productQtyMap));

      // DOM에서 메타필드 기반 max 값 읽기 + 버튼 상태 업데이트
      let isAllValid = true;
      const buttons = document.querySelectorAll('[data-main-cart-quantity-plus-button]');

      buttons.forEach(button => {
        const container = button.closest('[data-cart-items-main]');
        const productId = container.getAttribute('product-id');
        const accountMax = parseInt(container.getAttribute('data-purchase-limit-max'), 10);
        const orderMax = parseInt(container.getAttribute('data-order-limit-max'), 10);

        const hasAccount = accountMax && !isNaN(accountMax);
        const hasOrder = orderMax && !isNaN(orderMax);
        if (!hasAccount && !hasOrder) return; // 두 메타필드 모두 미설정 → 제한 없음

        const purchased = apiResults[productId]?.current_count || 0;
        const available = getEffectiveAvailable({
          accountMax: hasAccount ? accountMax : null,
          purchased,
          orderMax: hasOrder ? orderMax : null,
        });
        const cartQty = productQtyMap[productId] || 0;
        const isLimited = cartQty >= available;

        console.log('[PurchaseLimit] Cart -', productId,
                    '| accountMax:', accountMax, '| purchased:', purchased, '| orderMax:', orderMax,
                    '| available:', available, '| cartQty:', cartQty, '| limited:', isLimited);

        if (isLimited) {
          button.classList.add('quantity-up--disabled');
          button.style.pointerEvents = 'none';
          button.style.opacity = '0.5';
        } else {
          button.classList.remove('quantity-up--disabled');
          button.style.pointerEvents = '';
          button.style.opacity = '';
        }

        if (cartQty > available) {
          isAllValid = false;
        }
      });

      updateSubmitButtonState(isAllValid);
    } catch (error) {
      console.error('[PurchaseLimit] applyPurchaseLimits error:', error);
    }
  };

  // 연속 클릭 가드 - 캐시는 아래 초기 로드 단계에서 채워짐
  setupRapidClickGuard();

  // main-cart:quantitychanged 이벤트 (theme.js에서 2000ms 후 발생)
  document.addEventListener('main-cart:quantitychanged', async () => {
    await refreshPurchaseLimitCache();
    applyPurchaseLimits();
  });

  // --- 초기 로드: 초과 수량 자동 조정 ---
  const res = await fetch('/cart.js');
  const cartData = await res.json();

  const productMap = {};
  cartData.items.forEach(item => {
    const { product_id, variant_id, quantity, product_title, variant_title, title } = item;
    if (!productMap[product_id]) productMap[product_id] = [];
    productMap[product_id].push({ product_id, variant_id, quantity, product_title, variant_title, title });
  });

  const apiResults = await fetchPurchaseCounts(Object.keys(productMap));

  // DOM에서 두 메타필드 값 수집 + 연속 클릭 가드용 캐시 동기화
  const limitMap = {};
  document.querySelectorAll('[data-cart-items-main]').forEach(el => {
    const pid = el.getAttribute('product-id');
    const accountMax = parseInt(el.getAttribute('data-purchase-limit-max'), 10);
    const orderMax = parseInt(el.getAttribute('data-order-limit-max'), 10);

    const hasAccount = accountMax && !isNaN(accountMax);
    const hasOrder = orderMax && !isNaN(orderMax);
    if (!hasAccount && !hasOrder) return;

    const purchased = apiResults[pid]?.current_count || 0;
    const available = getEffectiveAvailable({
      accountMax: hasAccount ? accountMax : null,
      purchased,
      orderMax: hasOrder ? orderMax : null,
    });
    limitMap[pid] = available;
    __purchaseLimitCache[pid] = {
      accountMax: hasAccount ? accountMax : null,
      purchased,
      orderMax: hasOrder ? orderMax : null,
    };
  });

  // 초과 상품 자동 조정 목록 생성
  // 단일/다중 배리언트 모두 자동 조정 (첫 배리언트부터 허용 수량 채우고 나머지 0)
  const autoUpdates = [];
  let needsAdjustment = false;

  for (const [productId, variants] of Object.entries(productMap)) {
    const available = limitMap[productId];
    if (available === undefined) continue;

    const totalQty = variants.reduce((sum, v) => sum + v.quantity, 0);
    if (totalQty > available) {
      needsAdjustment = true;
      let remaining = available;
      variants.forEach(v => {
        const qty = Math.min(v.quantity, remaining);
        autoUpdates.push({ variantId: v.variant_id, quantity: qty });
        remaining = Math.max(0, remaining - qty);
      });
    }
  }

  if (needsAdjustment && window.location.pathname.includes('/cart')) {
    const attemptCount = parseInt(sessionStorage.getItem('purchase_limit_attempts') || '0', 10);
    if (attemptCount >= 2) {
      // 2회 이상 시도 실패 → 무한루프 방지, 안내만 표시
      sessionStorage.removeItem('purchase_limit_attempts');
      console.warn('[PurchaseLimit] Auto-adjustment failed after 2 attempts, skipping.');
      alert('최대구매수량을 초과한 상품이 있습니다. 수량을 조정해주세요.');
    } else {
      sessionStorage.setItem('purchase_limit_attempts', String(attemptCount + 1));
      const success = await applyAutoUpdates(autoUpdates);
      if (success) {
        alert('일부 상품이 최대구매수량을 초과하여 자동으로 조정되었습니다.');
        sessionStorage.removeItem('purchase_limit_attempts');
        location.reload();
        return;
      } else {
        alert('최대구매수량을 초과한 상품이 있습니다. 수량을 조정해주세요.');
      }
    }
  }

  // 초기 버튼 상태 적용
  await applyPurchaseLimits();
});
